
import React, { useEffect, useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '@/contexts/auth/AuthContext';
import { useToast } from "@/hooks/use-toast";
import { LoginHeader } from '@/components/auth/LoginHeader';
import { LoginForm } from '@/components/auth/LoginForm';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { supabase } from '@/integrations/supabase/client';
import { CheckCircle } from 'lucide-react';

const Login: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { isAuthenticated, loading } = useAuth();
  const { toast } = useToast();
  const [navigationError, setNavigationError] = useState<string | null>(null);
  const [redirectAttempted, setRedirectAttempted] = useState(false);
  const [showVerificationSuccess, setShowVerificationSuccess] = useState(false);

  // Check URL params for messages
  const queryParams = new URLSearchParams(location.search);
  const justVerified = queryParams.get('verified') === 'true';
  const invitationToken = queryParams.get('invitation_token');
  
  // Store invitation token if provided
  useEffect(() => {
    if (invitationToken) {
      localStorage.setItem('pending_invitation_token', invitationToken);
    }
  }, [invitationToken]);

  // Get the redirect path - check for pending invitation first
  const getPendingInvitationToken = () => localStorage.getItem('pending_invitation_token');
  const getRedirectPath = () => {
    const pendingToken = getPendingInvitationToken();
    if (pendingToken) {
      return `/accept-invitation?token=${pendingToken}`;
    }
    return (location.state as { from?: string })?.from || '/dashboard';
  };
  
  const from = getRedirectPath();
  const signupSuccess = queryParams.get('signup') === 'success';
  
  // Handle auto-login after email verification
  useEffect(() => {
    if (justVerified && !isAuthenticated && !loading) {
      console.log('Email verification detected, checking user status...');
      
      // Show verification success message immediately
      setShowVerificationSuccess(true);
      
      // Check if user is now verified and can be logged in
      supabase.auth.getUser().then(({ data: { user }, error }) => {
        if (user && user.email_confirmed_at) {
          console.log('User is verified, preparing welcome message...');
          
          // After showing verification success for 3 seconds, proceed with login
          setTimeout(() => {
            const userName = user.user_metadata?.name || user.email?.split('@')[0] || 'there';
            
            toast({
              title: `Welcome to ProptPilot, ${userName}! 🎉`,
              description: "Your email has been verified successfully. Welcome aboard!",
              duration: 5000,
            });
            
            // Wait a moment for the auth state to update, then redirect
            setTimeout(() => {
              if (isAuthenticated) {
                navigate(from, { replace: true });
              } else {
                // Force a refresh to trigger auth state update
                window.location.href = from;
              }
            }, 1000);
          }, 3000);
        } else if (error) {
          console.error('Error checking user verification:', error);
          setShowVerificationSuccess(false);
          toast({
            title: "Verification complete",
            description: "Please sign in with your credentials.",
          });
        } else {
          console.log('User not yet verified, hiding success message');
          setShowVerificationSuccess(false);
        }
      });
    }
  }, [justVerified, isAuthenticated, loading, navigate, from, toast]);

  useEffect(() => {
    // Set document title
    document.title = "Login - ProptPilot";
    
    // Show signup success message
    if (signupSuccess && !justVerified) {
      toast({
        title: "Registration successful",
        description: "Please check your email and click the verification link before logging in.",
        duration: 5000,
      });
    }
  }, [signupSuccess, justVerified, toast]);

  // Redirect if already authenticated (but not during verification flow)
  useEffect(() => {
    if (isAuthenticated && !loading && !redirectAttempted && !showVerificationSuccess && !justVerified) {
      try {
        setRedirectAttempted(true);
        navigate(from, { replace: true });
      } catch (err) {
        setNavigationError(`Failed to navigate: ${err instanceof Error ? err.message : String(err)}`);
      }
    }
  }, [isAuthenticated, loading, navigate, from, redirectAttempted, showVerificationSuccess, justVerified]);

  // Show verification success message
  if (showVerificationSuccess) {
    return (
      <div className="min-h-screen flex flex-col justify-center items-center relative overflow-hidden">
        {/* Background Image */}
        <div 
          className="absolute inset-0 bg-cover bg-center bg-no-repeat"
          style={{
            backgroundImage: `url('https://images.unsplash.com/photo-1560518883-ce09059eeffa?ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D&auto=format&fit=crop&w=2073&q=80')`
          }}
        />
        
        {/* Gradient Overlay */}
        <div className="absolute inset-0 bg-gradient-to-br from-white/95 via-white/90 to-primary/10" />
        
        {/* Content */}
        <div className="relative z-10 w-full max-w-md space-y-8 p-4">
          <div className="text-center">
            <h1 className="text-3xl font-bold text-gray-900 mb-2">ProptPilot</h1>
            <p className="text-gray-600">Your Complete Property Management Solution</p>
          </div>
          
          <div className="bg-card/95 backdrop-blur-sm p-6 rounded-lg shadow-xl border">
            <Alert className="border-green-200 bg-green-50">
              <CheckCircle className="h-5 w-5 text-green-600" />
              <AlertDescription className="text-green-800">
                <div className="space-y-2">
                  <p className="font-semibold text-lg">Email Verification Successful! ✅</p>
                  <p>Welcome to ProptPilot! Your account has been verified and you're being logged in automatically.</p>
                  <p className="text-sm text-green-700">Redirecting to your dashboard in a moment...</p>
                </div>
              </AlertDescription>
            </Alert>
            
            <div className="mt-4 flex justify-center">
              <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-primary"></div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Show loading state while checking auth
  if (loading) {
    return (
      <div className="flex items-center justify-center h-screen flex-col">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-primary mb-4"></div>
        <p className="text-sm text-muted-foreground">Checking authentication status...</p>
      </div>
    );
  }

  // Show navigation error if it occurs
  if (navigationError) {
    return (
      <div className="flex items-center justify-center h-screen flex-col p-4">
        <Alert variant="destructive" className="mb-4 max-w-md w-full">
          <AlertDescription>{navigationError}</AlertDescription>
        </Alert>
        <button 
          onClick={() => window.location.reload()} 
          className="px-4 py-2 bg-primary text-primary-foreground rounded"
        >
          Reload Page
        </button>
      </div>
    );
  }

  // If user is authenticated but still seeing the login page, force redirect
  if (isAuthenticated && !loading && !justVerified) {
    return (
      <div className="flex items-center justify-center h-screen flex-col">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-primary mb-4"></div>
        <p className="text-lg font-medium">Already logged in</p>
        <p className="text-sm text-muted-foreground mb-4">Redirecting to dashboard...</p>
        <button 
          onClick={() => navigate(from, { replace: true })}
          className="px-4 py-2 bg-primary text-primary-foreground rounded"
        >
          Go to Dashboard
        </button>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex flex-col justify-center items-center relative overflow-hidden">
      {/* Background Image */}
      <div 
        className="absolute inset-0 bg-cover bg-center bg-no-repeat"
        style={{
          backgroundImage: `url('https://images.unsplash.com/photo-1560518883-ce09059eeffa?ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D&auto=format&fit=crop&w=2073&q=80')`
        }}
      />
      
      {/* Gradient Overlay */}
      <div className="absolute inset-0 bg-gradient-to-br from-white/95 via-white/90 to-primary/10" />
      
      {/* Content */}
      <div className="relative z-10 w-full max-w-md space-y-8 p-4">
        <LoginHeader />
        
        {/* Show verification reminder for new signups */}
        {signupSuccess && !justVerified && (
          <Alert className="bg-blue-50 border-blue-200 text-blue-800">
            <AlertDescription>
              Please check your email and click the verification link before logging in.
            </AlertDescription>
          </Alert>
        )}
        
        <LoginForm justVerified={justVerified} from={from} />
      </div>
    </div>
  );
};

export default Login;
